/**
 * Product-owned mapping between Event Creator choices and tags in the
 * published-template catalog. A signal scores a template once when at least
 * one of its tags matches; extra synonymous tags add only a small bonus.
 */
export const TILE_RECOMMENDATION_SIGNALS = {
    venue_trade_show: {
        weight: 6,
        tags: ['events', 'engaging_users_at_the_booth', 'touchscreen_games'],
    },
    venue_conference: {
        weight: 6,
        tags: ['events', 'interactive_presentations', 'audience_engagement'],
    },
    venue_in_store: {
        weight: 6,
        tags: ['retail', 'touchscreen_games', 'giveaway_for_visitors', 'product_promotion'],
    },
    venue_corporate_event: {
        weight: 6,
        tags: ['employee_engagement', 'team_building', 'icebreakers', 'events'],
    },
    venue_public_event: {
        weight: 6,
        tags: ['events', 'holiday_event', 'audience_engagement', 'entertainment'],
    },
    venue_online_event: {
        weight: 6,
        tags: ['activities_for_video_calls', 'interactive_presentations', 'audience_engagement'],
    },

    goal_engage_audience: {
        weight: 10,
        tags: ['audience_engagement', 'marketing_games', 'engaging_users_at_the_booth', 'interactive_giveaways'],
    },
    goal_collect_leads: {
        weight: 10,
        tags: ['lead_generation', 'gamified_lead_capture', 'lead_form', 'lead_magnets', 'email_marketing'],
    },
    goal_prize_giveaway: {
        weight: 10,
        tags: [
            'giveaways',
            'interactive_giveaways',
            'discounts_and_prize_draws',
            'giveaway_for_visitors',
            'fortune_wheel',
        ],
    },
    goal_promote_product: {
        weight: 10,
        tags: ['product_promotion', 'marketing_campaigns', 'brand_awareness', 'perfomance_marketing'],
    },
    goal_educate_audience: {
        weight: 10,
        tags: ['education', 'interactive_worksheets', 'trivia_quiz'],
    },
    goal_team_engagement: {
        weight: 10,
        tags: ['employee_engagement', 'team_building', 'icebreakers', 'live_games'],
    },

    mechanic_spin_wheel: {
        weight: 16,
        tags: ['fortune_wheel'],
        diversityGroup: 'fortune_wheel',
    },
    mechanic_quiz: {
        weight: 16,
        tags: ['trivia_quiz'],
        diversityGroup: 'trivia_quiz',
    },
    mechanic_memory: {
        weight: 16,
        tags: ['memory_games'],
        diversityGroup: 'memory_games',
    },
    mechanic_instant_win: {
        weight: 16,
        tags: ['interactive_giveaways', 'giveaways', 'discounts_and_prize_draws'],
        diversityGroup: 'instant_win',
    },
    mechanic_leaderboard: {
        weight: 16,
        tags: ['rank_battle', 'live_games'],
        diversityGroup: 'leaderboard',
    },
    mechanic_puzzle: {
        weight: 16,
        tags: ['puzzle', 'sliding_puzzle', 'match_up', 'hidden_objects', 'crossword'],
        diversityGroup: 'puzzle',
    },

    participation_walk_up: {
        weight: 5,
        tags: ['touchscreen_games', 'touchscreen_game'],
    },
    participation_host_led: {
        weight: 5,
        tags: ['live_games', 'interactive_presentations'],
        preferredMode: 'MULTIPLAYER',
    },
    participation_participant_phones: {
        weight: 5,
        tags: ['live_games', 'audience_engagement'],
        preferredMode: 'MULTIPLAYER',
    },
    participation_team_game: {
        weight: 5,
        tags: ['team_building', 'live_games', 'employee_engagement'],
        preferredMode: 'MULTIPLAYER',
    },
    participation_continuous_flow: {
        weight: 5,
        tags: ['touchscreen_games', 'giveaway_for_visitors'],
    },

    device_touchscreen: {
        weight: 5,
        tags: ['touchscreen_games', 'touchscreen_game'],
    },
    device_tablet: {
        weight: 5,
        tags: ['touchscreen_games', 'touchscreen_game'],
    },
    device_large_screen: {
        weight: 5,
        tags: ['interactive_presentations', 'live_games'],
    },
    device_kiosk: {
        weight: 5,
        tags: ['touchscreen_games', 'touchscreen_game'],
    },
    device_participant_phones: {
        weight: 5,
        tags: ['live_games', 'audience_engagement'],
        preferredMode: 'MULTIPLAYER',
    },

    leads_none: {
        excludedTags: ['lead_generation', 'gamified_lead_capture', 'lead_form', 'lead_magnets', 'email_marketing'],
    },
    leads_basic_contact: {
        weight: 8,
        tags: ['lead_generation', 'lead_form', 'gamified_lead_capture'],
    },
    leads_custom_form: {
        weight: 8,
        tags: ['lead_form', 'lead_generation', 'gamified_lead_capture'],
    },
    leads_before_game: {
        weight: 8,
        tags: ['lead_generation', 'lead_form', 'gamified_lead_capture'],
    },
    leads_after_game: {
        weight: 8,
        tags: ['lead_generation', 'lead_form', 'gamified_lead_capture'],
    },
    leads_consent: {
        weight: 8,
        tags: ['lead_form', 'lead_generation', 'email_marketing'],
    },

    rewards_none: {
        excludedTags: ['giveaways', 'interactive_giveaways', 'discounts_and_prize_draws', 'giveaway_for_visitors'],
    },
    rewards_instant_prize: {
        weight: 8,
        tags: ['interactive_giveaways', 'giveaways', 'fortune_wheel'],
    },
    rewards_prize_draw: {
        weight: 8,
        tags: ['discounts_and_prize_draws', 'giveaways'],
    },
    rewards_limited_inventory: {
        weight: 8,
        tags: ['interactive_giveaways', 'giveaways'],
    },
    rewards_promo_code: {
        weight: 8,
        tags: ['discounts_and_prize_draws', 'fortune_wheel'],
    },
    rewards_winner_log: {
        weight: 8,
        tags: ['giveaways', 'interactive_giveaways'],
    },

    scale_multiple_screens: {
        weight: 2,
        tags: ['live_games', 'touchscreen_games'],
    },
    scale_event_series: {
        weight: 2,
        tags: ['marketing_campaigns'],
    },
}

/**
 * Free text supplements explicit choices. These groups intentionally describe
 * reusable event intent rather than individual template names.
 */
export const DESCRIPTION_RECOMMENDATION_SIGNALS = [
    {
        phrases: ['giveaway', 'prize', 'win', 'winner', 'coupon', 'discount', 'розыгрыш', 'приз', 'скидк'],
        tags: ['giveaways', 'interactive_giveaways', 'discounts_and_prize_draws', 'giveaway_for_visitors'],
    },
    {
        phrases: ['quiz', 'trivia', 'competition', 'викторин', 'квиз', 'конкурс'],
        tags: ['trivia_quiz', 'rank_battle'],
    },
    {
        phrases: ['wheel', 'spin', 'колес', 'крут'],
        tags: ['fortune_wheel'],
    },
    {
        phrases: ['memory', 'matching pairs', 'памят', 'найти пару'],
        tags: ['memory_games', 'match_up'],
    },
    {
        phrases: ['puzzle', 'challenge', 'crossword', 'hidden object', 'головолом', 'пазл', 'кроссворд'],
        tags: ['puzzle', 'sliding_puzzle', 'match_up', 'hidden_objects', 'crossword'],
    },
    {
        phrases: ['lead', 'email', 'contact', 'form', 'лид', 'почт', 'контакт', 'форм'],
        tags: ['lead_generation', 'gamified_lead_capture', 'lead_form', 'lead_magnets', 'email_marketing'],
    },
    {
        phrases: ['booth', 'exhibition', 'trade show', 'expo', 'стенд', 'выставк'],
        tags: ['events', 'engaging_users_at_the_booth', 'touchscreen_games'],
    },
    {
        phrases: ['store', 'retail', 'shop', 'магазин', 'ритейл'],
        tags: ['retail', 'touchscreen_games', 'product_promotion'],
    },
    {
        phrases: ['corporate', 'employee', 'team building', 'корпоратив', 'сотрудник', 'команд'],
        tags: ['employee_engagement', 'team_building', 'icebreakers'],
    },
    {
        phrases: ['online', 'hybrid', 'video call', 'zoom', 'онлайн', 'гибрид'],
        tags: ['activities_for_video_calls', 'interactive_presentations', 'audience_engagement'],
    },
    {
        phrases: ['product', 'brand', 'promotion', 'продукт', 'бренд', 'продвиж'],
        tags: ['product_promotion', 'brand_awareness', 'marketing_campaigns'],
    },
    {
        phrases: ['touchscreen', 'touch screen', 'kiosk', 'тачскрин', 'сенсорн', 'киоск'],
        tags: ['touchscreen_games', 'touchscreen_game'],
    },
    {
        phrases: ['leaderboard', 'ranking', 'рейтинг', 'лидер'],
        tags: ['rank_battle', 'live_games'],
    },
]

export const RECOMMENDATION_CONFIG = {
    resultCount: 3,
    extraTagMatchFactor: 0.2,
    excludedTagPenalty: 24,
    preferredModeBonus: 3,
    descriptionSignalWeight: 4,
    descriptionNameTokenWeight: 0.75,
    maxDescriptionNameTokenScore: 3,
    preferredLanguageBonus: 2.5,
    languageNeutralBonus: 1.5,
    englishFallbackBonus: 0.5,
    popularTagBonus: 1,
    maxPopularityBonus: 1.25,
}
